import React, { useState } from "react";
import { API_BASE, getProductStock, decreaseProductStock } from "../services/api";
import { showErrorToast, showSuccessToast } from "../services/toast";

export function InquiryDetailsModal({
  isOpen,
  onClose,
  inquiryData,
  loading,
  error,
  onStockUpdated,
}) {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [reduceAmount, setReduceAmount] = useState(1);
  const [note, setNote] = useState("");
  const [fetchingStock, setFetchingStock] = useState(false);
  const [stockUpdating, setStockUpdating] = useState(false);

  if (!isOpen) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return isNaN(date.getTime())
      ? dateStr
      : date.toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
  };

  // 1. Trigger stock check and open confirmation modal
  const handleOpenStockConfirm = async (item) => {
    const productId = item.product_id || item.id || item._id;
    const initialQuantity = item.boxes_needed || 1;

    setFetchingStock(true);
    try {
      // Call GET /retailer/products/{product_id}/stock
      const stockRes = await getProductStock(productId);
      const stockData = stockRes.data;

      setSelectedProduct({
        ...item,
        product_id: productId,
        stock_quantity: stockData.stock_quantity,
        stock_status: stockData.stock_status,
      });
      setReduceAmount(initialQuantity);
      setNote(`Stock decreased from Inquiry ID: ${inquiryData.id || inquiryData._id || ""}`);
    } catch (err) {
      showErrorToast(err.message || "Failed to fetch live product stock");
    } finally {
      setFetchingStock(false);
    }
  };

  // 2. Submit POST /retailer/products/{product_id}/decrease-stock
  const handleConfirmStockReduction = async () => {
    if (!selectedProduct) return;

    setStockUpdating(true);
    try {
      const payload = {
        quantity: Number(reduceAmount),
        inquiry_id: inquiryData.id || inquiryData._id || null,
        note: note.trim() || undefined,
      };

      const res = await decreaseProductStock(selectedProduct.product_id, payload);

      showSuccessToast(res.message || "Product stock decreased successfully!");

      // Update local state value for quick UI update
      selectedProduct.stock_quantity = res.data.stock_quantity;
      setSelectedProduct(null);

      if (typeof onStockUpdated === "function") {
        onStockUpdated();
      }
    } catch (err) {
      showErrorToast(err.message || "Failed to decrease stock");
    } finally {
      setStockUpdating(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between border-b pb-4 mb-4">
            <h2 className="text-base font-bold uppercase tracking-wide text-gray-900">
              Inquiry & Product Details
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-lg font-bold"
            >
              ✕
            </button>
          </div>

          {/* Loading / Error States */}
          {loading && (
            <div className="py-12 text-center text-xs text-gray-500">
              Fetching inquiry details...
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-3 text-xs text-red-600">
              {error}
            </div>
          )}

          {!loading && !error && inquiryData && (
            <div className="space-y-6 text-xs text-gray-700">
              {/* Customer Info Table */}
              <div>
                <h3 className="mb-2 text-xs font-bold text-gray-800">
                  Customer & Inquiry Information
                </h3>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full border-collapse text-left">
                    <tbody className="divide-y divide-gray-200">
                      <tr className="bg-gray-50/50">
                        <td className="py-2 px-3 font-semibold text-gray-600 w-1/4">Inquiry ID</td>
                        <td className="py-2 px-3">{inquiryData.id || inquiryData._id || "-"}</td>
                        <td className="py-2 px-3 font-semibold text-gray-600 w-1/4">Created At</td>
                        <td className="py-2 px-3 font-medium text-gray-900">
                          {formatDate(inquiryData.created_at || inquiryData.createdAt)}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2 px-3 font-semibold text-gray-600">Full Name/Company Name</td>
                        <td className="py-2 px-3">
                          {inquiryData.full_name || inquiryData.customer_name || "-"}
                        </td>
                        <td className="py-2 px-3 font-semibold text-gray-600">Email</td>
                        <td className="py-2 px-3">
                          {inquiryData.email || inquiryData.customer_email || "-"}
                        </td>
                      </tr>
                      <tr className="bg-gray-50/50">
                        <td className="py-2 px-3 font-semibold text-gray-600">Contact Number</td>
                        <td className="py-2 px-3">
                          {inquiryData.contact_country_code || ""}{" "}
                          {inquiryData.contact_number || inquiryData.phone || "-"}
                        </td>
                        <td className="py-2 px-3 font-semibold text-gray-600">Location</td>
                        <td className="py-2 px-3">
                          {[inquiryData.city, inquiryData.state, inquiryData.country]
                            .filter(Boolean)
                            .join(", ") || "-"}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2 px-3 font-semibold text-gray-600">Status</td>
                        <td className="py-2 px-3 uppercase font-semibold text-indigo-600">
                          {inquiryData.status || "New"}
                        </td>
                        <td className="py-2 px-3 font-semibold text-gray-600">Website</td>
                        <td className="py-2 px-3">{inquiryData.website_url || "-"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Inquired Products List */}
              <div>
                <h3 className="mb-2 text-xs font-bold text-gray-800">Inquired Products List</h3>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full border-collapse text-left">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-100 text-gray-700 font-semibold">
                        <th className="py-2 px-3">#</th>
                        <th className="py-2 px-3">Image</th>
                        <th className="py-2 px-3">Product Name</th>
                        <th className="py-2 px-3">SKU</th>
                        <th className="py-2 px-3">Boxes Needed</th>
                        <th className="py-2 px-3">Total Price</th>
                        <th className="py-2 px-3 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {inquiryData.items && inquiryData.items.length > 0 ? (
                        inquiryData.items.map((item, idx) => {
                          const rawUrl =
                            Array.isArray(item.images) && item.images.length > 0
                              ? item.images[0]?.url || item.images[0]
                              : item.image_url || item.image;

                          const fullImageUrl = rawUrl
                            ? rawUrl.startsWith("http")
                              ? rawUrl
                              : `${API_BASE}${rawUrl.startsWith("/") ? "" : "/"}${rawUrl}`
                            : null;

                          return (
                            <tr key={idx} className="hover:bg-gray-50/50">
                              <td className="py-2 px-3">{idx + 1}</td>
                              <td className="py-2 px-3">
                                {fullImageUrl ? (
                                  <img
                                    src={fullImageUrl}
                                    alt={item.product_name || "Product"}
                                    className="h-12 w-12 rounded-md border border-gray-200 object-cover"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = "https://via.placeholder.com/48?text=No+Image";
                                    }}
                                  />
                                ) : (
                                  <div className="flex h-12 w-12 items-center justify-center rounded-md border border-gray-200 bg-gray-100 text-[10px] text-gray-400">
                                    No Image
                                  </div>
                                )}
                              </td>
                              <td className="py-2 px-3 font-medium text-gray-900">
                                {item.product_name || "-"}
                              </td>
                              <td className="py-2 px-3">{item.product_sku || "-"}</td>
                              <td className="py-2 px-3 font-semibold text-gray-900">
                                {item.boxes_needed ?? "-"}
                              </td>
                              <td className="py-2 px-3 font-semibold text-gray-900">
                                {item.total_price ?? "-"}
                              </td>
                              <td className="py-2 px-3 text-center">
                                <button
                                  type="button"
                                  disabled={fetchingStock}
                                  onClick={() => handleOpenStockConfirm(item)}
                                  className="px-2.5 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 rounded-md transition-colors disabled:opacity-50"
                                >
                                  Decrease Stock
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="7" className="py-4 text-center text-gray-500">
                            No product details found for this inquiry.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Modal Action Footer */}
          <div className="mt-6 flex justify-end border-t pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-gray-600 px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Decrease Stock Confirmation Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl space-y-4">
            <h3 className="text-base font-bold text-gray-900 border-b pb-2">
              Confirm Decrease Stock
            </h3>

            <div className="space-y-3 text-xs text-gray-700">
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-1">
                <div>
                  <span className="font-semibold text-gray-600">Product: </span>
                  <span className="font-bold text-gray-900">
                    {selectedProduct.product_name || "-"}
                  </span>
                </div>
                <div>
                  <span className="font-semibold text-gray-600">SKU: </span>
                  <span className="font-mono text-gray-800">
                    {selectedProduct.product_sku || "-"}
                  </span>
                </div>
              </div>

              {/* Stock Quantity Details */}
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="p-2.5 bg-gray-100 rounded-md">
                  <div className="text-[10px] uppercase text-gray-500 font-semibold">
                    Current Stock
                  </div>
                  <div className="text-base font-bold text-gray-800">
                    {selectedProduct.stock_quantity}
                  </div>
                </div>

                <div className="p-2.5 bg-amber-50 rounded-md border border-amber-200">
                  <div className="text-[10px] uppercase text-amber-700 font-semibold">
                    Stock After Reduction
                  </div>
                  <div className="text-base font-bold text-amber-700">
                    {Math.max(0, selectedProduct.stock_quantity - Number(reduceAmount))}
                  </div>
                </div>
              </div>

              {/* Quantity Input */}
              <div>
                <label className="block font-semibold text-gray-700 mb-1">
                  Quantity to Decrease:
                </label>
                <input
                  type="number"
                  min="1"
                  max={selectedProduct.stock_quantity}
                  value={reduceAmount}
                  onChange={(e) => setReduceAmount(Math.max(1, Number(e.target.value)))}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                />
              </div>

              {/* Optional Note Input */}
              {/* <div>
                <label className="block font-semibold text-gray-700 mb-1">
                  Note (Optional):
                </label>
                <input
                  type="text"
                  placeholder="e.g. Dispatched for inquiry order"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                />
              </div> */}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-3 border-t">
              <button
                type="button"
                onClick={() => setSelectedProduct(null)}
                disabled={stockUpdating}
                className="px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmStockReduction}
                disabled={
                  stockUpdating ||
                  reduceAmount <= 0 ||
                  selectedProduct.stock_quantity < reduceAmount
                }
                className="px-4 py-1.5 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-md transition-colors disabled:opacity-50"
              >
                {stockUpdating ? "Updating..." : "Confirm Reduction"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}